import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Trophy,
  Target,
  Zap,
  Calendar,
  Clock,
  TrendingUp,
  Award,
  Star,
  Flame,
  Moon,
  Coffee,
  CheckCircle2,
  BarChart3,
  Timer,
  Medal,
} from "lucide-react";
import { AppDispatch } from "../../Redux/Store/Store";
import { ListAchievements } from "../../Redux/Slices/DashboardData/Achievements";
import { useAppSelector } from "../../Redux/Store/Hooks";

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: "practice" | "performance" | "consistency" | "special";
  isUnlocked: boolean;
  progress?: number;
  target?: number;
}

const AchievementsGrid = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: achievementresponse } = useAppSelector(
    (state) => state.AchievementsSlicer
  );

  console.log(achievementresponse, "res acheviments");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] =
    useState<Achievement | null>(null);

  useEffect(() => {
    dispatch(ListAchievements());
  }, [dispatch]);

  const achievementConfig: Record<
    string,
    {
      title: string;
      description: string;
      icon: React.ReactNode;
      category: "practice" | "performance" | "consistency" | "special";
    }
  > = {
    "Question Grinder": {
      title: "Question Grinder",
      description: "Complete 500 Questions in total.",
      icon: <Target size={20} />,
      category: "practice",
    },
    "Mini Mock Explorer": {
      title: "Mini Mock Explorer",
      description: "Complete 20 Mini Mocks.",
      icon: <CheckCircle2 size={20} />,
      category: "practice",
    },
    "Test Finisher": {
      title: "Test Finisher",
      description: "Complete 50 Tests.",
      icon: <Award size={20} />,
      category: "practice",
    },
    "Exam Veteran": {
      title: "Exam Veteran",
      description: "Complete 10 Exams.",
      icon: <Medal size={20} />,
      category: "practice",
    },
    "Daily Starter": {
      title: "Daily Starter",
      description: "Complete at least 1 activity for 7 consecutive days.",
      icon: <Calendar size={20} />,
      category: "practice",
    },
    "Accuracy Master": {
      title: "Accuracy Master",
      description: "Achieve 90%+ accuracy on 5 Quizzes.",
      icon: <Star size={20} />,
      category: "performance",
    },
    "Perfect Streak": {
      title: "Perfect Streak",
      description: "Achieve 100% accuracy on any full Test.",
      icon: <Trophy size={20} />,
      category: "performance",
    },
    "Comeback Kid": {
      title: "Comeback Kid",
      description:
        "Improve average accuracy by 15% across your last 10 Quizzes.",
      icon: <TrendingUp size={20} />,
      category: "performance",
    },
    "Speed Demon": {
      title: "Speed Demon",
      description: "Complete a full Quiz in under 50% of the allotted time.",
      icon: <Zap size={20} />,
      category: "performance",
    },
    "Top 10": {
      title: "Top 10%",
      description: "Score in the top 10% percentile on any Exam.",
      icon: <BarChart3 size={20} />,
      category: "performance",
    },
    "Streak Beginner": {
      title: "Streak Beginner",
      description: "Maintain a 3-day activity streak.",
      icon: <Flame size={20} />,
      category: "consistency",
    },
    "Streak Master": {
      title: "Streak Master",
      description: "Maintain a 14-day activity streak.",
      icon: <Flame size={20} />,
      category: "consistency",
    },
    "Streak Legend": {
      title: "Streak Legend",
      description: "Maintain a 30-day activity streak.",
      icon: <Flame size={20} />,
      category: "consistency",
    },
    "Weekend Warrior": {
      title: "Weekend Warrior",
      description: "Complete activities on 4 consecutive weekends.",
      icon: <Coffee size={20} />,
      category: "consistency",
    },
    "Night Owl": {
      title: "Night Owl",
      description: "Complete activities after 9 PM on 7 separate days.",
      icon: <Moon size={20} />,
      category: "consistency",
    },
    // Handle different key formats from API
    accuracy_master: {
      title: "Accuracy Master",
      description: "Achieve 90%+ accuracy on 5 Quizzes.",
      icon: <Star size={20} />,
      category: "performance",
    },
    comeback_kid: {
      title: "Comeback Kid",
      description:
        "Improve average accuracy by 15% across your last 10 Quizzes.",
      icon: <TrendingUp size={20} />,
      category: "performance",
    },
    perfect_streak: {
      title: "Perfect Streak",
      description: "Achieve 100% accuracy on any full Test.",
      icon: <Trophy size={20} />,
      category: "performance",
    },
    speed_demon: {
      title: "Speed Demon",
      description: "Complete a full Quiz in under 50% of the allotted time.",
      icon: <Zap size={20} />,
      category: "performance",
    },
    top_10_percent: {
      title: "Top 10%",
      description: "Score in the top 10% percentile on any Exam.",
      icon: <BarChart3 size={20} />,
      category: "performance",
    },
    streak_beginner: {
      title: "Streak Beginner",
      description: "Maintain a 3-day activity streak.",
      icon: <Flame size={20} />,
      category: "consistency",
    },
    streak_master: {
      title: "Streak Master",
      description: "Maintain a 14-day activity streak.",
      icon: <Flame size={20} />,
      category: "consistency",
    },
    streak_legend: {
      title: "Streak Legend",
      description: "Maintain a 30-day activity streak.",
      icon: <Flame size={20} />,
      category: "consistency",
    },
    weekend_warrior: {
      title: "Weekend Warrior",
      description: "Complete activities on 4 consecutive weekends.",
      icon: <Coffee size={20} />,
      category: "consistency",
    },
    night_owl: {
      title: "Night Owl",
      description: "Complete activities after 9 PM on 7 separate days.",
      icon: <Moon size={20} />,
      category: "consistency",
    },
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "practice":
        return "#2196F3";
      case "performance":
        return "#4CAF50";
      case "consistency":
        return "#FF9800";
      case "special":
        return "#9C27B0";
      default:
        return "#757575";
    }
  };

  const getCategoryName = (category: string) => {
    switch (category) {
      case "practice":
        return "Practice Milestones";
      case "performance":
        return "Performance Milestones";
      case "consistency":
        return "Consistency/Streaks";
      case "special":
        return "Special Events";
      default:
        return "Other";
    }
  };

  if (!achievementresponse?.data) return null;
  // Convert Redux data to Achievement array
  const achievements: Achievement[] = Object.entries(
    achievementresponse.data
  ).flatMap(([categoryKey, categoryAchievements]) => {
    if (
      typeof categoryAchievements !== "object" ||
      categoryAchievements === null
    ) {
      return [];
    }
    return Object.entries(categoryAchievements as Record<string, boolean>).map(
      ([name, isUnlocked]) => {
        const config = achievementConfig[name] || {
          title: name.replace(/_/g, " "),
          description: "description not available.",
          icon: <Award size={20} />,
          category: categoryKey.toLowerCase().includes("practice")
            ? "practice"
            : categoryKey.toLowerCase().includes("performance")
            ? "performance"
            : categoryKey.toLowerCase().includes("consistency")
            ? "consistency"
            : "special",
        };

        return {
          id: name,
          title: config.title,
          description: config.description,
          icon: config.icon,
          category: config.category,
          isUnlocked: Boolean(isUnlocked),
        };
      }
    );
  });

  const groupedAchievements = achievements.reduce((acc, achievement) => {
    if (!acc[achievement.category]) {
      acc[achievement.category] = [];
    }
    acc[achievement.category].push(achievement);
    return acc;
  }, {} as Record<string, Achievement[]>);

  // Group achievements into rows of 3 for the calendar layout
  const groupedAchievementsArray: Achievement[][] = [];
  for (let i = 0; i < achievements.length; i += 3) {
    groupedAchievementsArray.push(achievements.slice(i, i + 3));
  }

  // Show first 4 rows (12 achievements) on main screen
  const firstFourRows = groupedAchievementsArray.slice(0, 4);

  const handleAchievementClick = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
  };

  const handleClosePopup = () => {
    setSelectedAchievement(null);
  };

  const renderAchievementCard = (achievement: Achievement) => (
    <Tooltip
      title={
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            fontSize="14px"
          >
            {achievement.title}
          </Typography>
          <Typography variant="body2" fontSize="12px">
            {achievement.isUnlocked
              ? achievement.description
              : `Locked – ${achievement.description}`}
          </Typography>
        </Box>
      }
      key={achievement.id}
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "background.paper",
            color: "text.primary",
            boxShadow: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px",
            maxWidth: 250,
            p: 1.5,
          },
        },
        arrow: {
          sx: {
            color: "background.paper",
            "&::before": {
              border: "1px solid",
              borderColor: "divider",
            },
          },
        },
      }}
    >
      <Card
        sx={{
          borderRadius: "8px",
          boxShadow: 2,
          border: "1px solid",
          borderColor: achievement.isUnlocked ? "#009dff33" : "#ddd",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: "8px",
          gap: "3px",
          cursor: "pointer",
          transition: "transform 0.2s",
          height: "100%",
          maxwidth: "120px",
          width: "100%",
          backgroundColor: achievement.isUnlocked ? "#e6f4ff" : "#f5f5f5",
          "&:hover": {
            transform: "scale(1.06)",
            boxShadow: 3,
          },
        }}
        onClick={() => handleAchievementClick(achievement)}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              filter: achievement.isUnlocked ? "none" : "grayscale(100%)",
              opacity: achievement.isUnlocked ? 1 : 0.6,
              backgroundColor: achievement.isUnlocked ? "#ffffff" : "#e0e0e0",
              color: achievement.isUnlocked
                ? getCategoryColor(achievement.category)
                : "#aaa",
            }}
            variant="square"
          >
            {achievement.icon}
          </Avatar>
        </Box>
        <CardContent
          sx={{
            p: 0,
            textAlign: "center",
            paddingBottom: "0px !important",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontSize: "12px",
              lineHeight: 1.2,
              paddingBottom: "0px !important",
              color: achievement.isUnlocked ? "text.primary" : "text.disabled",
            }}
          >
            {achievement.title}
          </Typography>
        </CardContent>
      </Card>
    </Tooltip>
  );

  return (
    <Box>
      {/* Calendar-style grid (first 4 rows only) */}
      <Box sx={{ mb: 3 }}>
        {firstFourRows.map((row, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              mb: rowIndex !== firstFourRows.length - 1 ? 2.5 : 0,
              position: "relative",
            }}
          >
            <Grid container spacing={1}>
              {row.map((achievement, index) => (
                <Grid item xs={12} sm={4} key={`${achievement.id}-${index}`}>
                  {renderAchievementCard(achievement)}
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>

      {/* View All Button */}
      <Box textAlign="center" mt={2}>
        <Button
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "0.875rem",
            borderColor: "none",
          }}
        >
          View all {achievements.length} achievements
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px", // rounded corners
            boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)", // subtle white shadow
            backgroundColor: "#fff",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "#ffffffcc",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "1.25rem",
            px: 3,
            py: 2,
          }}
        >
          All Achievements
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers={false}
          sx={{
            px: 3,
            pb: 3,
          }}
        >
          <Box sx={{ mt: 2 }}>
            {Object.entries(groupedAchievements).map(
              ([category, categoryAchievements]) => (
                <Box key={category} sx={{ mb: 4 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      mb: 2,
                    }}
                  >
                    {getCategoryName(category)}
                  </Typography>
                  <Grid container spacing={2}>
                    {categoryAchievements.map((achievement) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={2.4}
                        key={achievement.id}
                      >
                        {renderAchievementCard(achievement)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AchievementsGrid;
